"use client";

// Reps : le chemin du parcours façon Duolingo — PARITÉ EXACTE avec l'app
// mobile (src/features/path/PathScreen.tsx) : bannières d'unité sticky
// colorées (couleur du parcours), nœuds 3D posés sur un zigzag découpé en
// virages entiers (lib/reps/pathShape.js — la forme s'adapte au nombre de
// leçons de l'unité et se poursuit d'une unité à l'autre),
// décorations admin dans les creux (noir et blanc si unité verrouillée),
// callout ancré au nœud cliqué, centrage automatique sur la leçon courante
// et bouton de recentrage quand elle sort de l'écran.
// La progression vient du SERVEUR (track.progress.completedLessonSlugs) :
// déblocage séquentiel, une seule leçon "current".

import {
    Fragment,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Link, useRouter as useLocalizedRouter } from "@/i18n/navigation";
import ProgressFill from "@/components/ui/Button/ProgressFill";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import RepsPathDecoration, {
    decorationsForUnit,
} from "@/components/Reps/RepsPathDecoration";
import RepsMatchMadness from "@/components/Reps/RepsMatchMadness";
import RepsUnitSkip from "@/components/Reps/RepsUnitSkip";
import { hasUnitSkip } from "@/lib/reps/access";
import { useSession } from "next-auth/react";
import {
    LEGENDARY_COLORS,
    LEGENDARY_FACETS,
    LEGENDARY_XP,
    legendaryDoneMap,
} from "@/lib/reps/legendary";
import {
    nextUnitSign,
    unitDecorationSlots,
    unitOffsets,
} from "@/lib/reps/pathShape";

/** XP promis dans les CTA (mêmes valeurs que l'app mobile). */
const REWARD_XP = 20;
const REVIEW_XP = 5;

// Départ différé d'un CTA de leçon : la barre rampe jusqu'à mi-course
// (NAV_FILL_MS), fonce à 100 % (complétion du hook), et la navigation ne part
// qu'à NAV_PUSH_MS, barre pleine. Sans ce différé, une leçon préchargée
// arrivait en ~200 ms et coupait la barre avant le milieu du bouton — un
// remplissage interrompu raconte un échec, pas un départ.
const NAV_FILL_MS = 280;
const NAV_PUSH_MS = 520;
/** Épaisseur de la lèvre 3D des nœuds / enfoncement au press. */
const NODE_LIP = 8;
/** Perspective Duolingo : la face des nœuds est une ELLIPSE écrasée (vue de
 * léger surplomb), pas un cercle — hauteur = largeur × ce ratio. L'icône
 * intérieure est écrasée du même ratio (même plan incliné). */
const NODE_SQUASH = 0.86;
/** Header du site : sticky h-[65px] — les bannières d'unité se collent dessous. */
const STICKY_TOP = 65;
/** Recentrage FAB : nœud posé à 220px du haut (parité mobile RECENTER_OFFSET,
 * le callout garde sa place en dessous). */
const RECENTER_OFFSET = 220;
/** Air laissé sous la bulle quand on la révèle : les boutons flottants du bas
 * (inventaire à gauche sous lg, recentrage à droite) sont posés à 24px du bas
 * et mesurent 48px — le CTA de la bulle ne doit jamais finir dessous. */
const CALLOUT_CLEARANCE = 88;
/** Touches qui font défiler la page : elles referment la bulle au même titre
 * qu'un geste de molette ou de doigt. */
const SCROLL_KEYS = new Set([
    " ",
    "PageUp",
    "PageDown",
    "Home",
    "End",
    "ArrowUp",
    "ArrowDown",
]);

/** Numéro d'unité depuis la clé "…unite-N…" (fallback : position). */
function unitNumber(key, index) {
    const m = /unite?-(\d+)/i.exec(key || "");
    return m ? parseInt(m[1], 10) : index + 1;
}

/** Premier ancêtre scrollable (le contenu du site défile dans un conteneur
 * interne, pas dans la fenêtre). */
function scrollParent(el) {
    let cur = el.parentElement;
    while (cur) {
        const cs = getComputedStyle(cur);
        if (
            (cs.overflowY === "auto" || cs.overflowY === "scroll") &&
            cur.scrollHeight > cur.clientHeight
        ) {
            return cur;
        }
        cur = cur.parentElement;
    }
    return null;
}

/** Défile un conteneur jusqu'à une position, animé ou non — avec le garde-fou
 * des environnements qui n'animent pas les scrolls programmés : si rien n'a
 * bougé peu après la demande, on saute à la cible. */
function scrollTopTo(scroller, target, smooth) {
    const from = scroller.scrollTop;
    if (Math.abs(target - from) < 2) return;
    if (!smooth) {
        scroller.scrollTo({ top: target });
        return;
    }
    scroller.scrollTo({ top: target, behavior: "smooth" });
    window.setTimeout(() => {
        if (Math.abs(scroller.scrollTop - from) < 2) {
            scroller.scrollTo({ top: target });
        }
    }, 250);
}

/** Amène un élément à une position donnée de son conteneur de scroll —
 * centré (topOffset null) ou posé à topOffset px du haut. Le calcul est
 * manuel : scrollIntoView({behavior:"smooth"}) est capricieux sur les
 * conteneurs imbriqués. */
function centerInScroller(el, smooth, topOffset = null) {
    const scroller = scrollParent(el);
    if (!scroller) {
        el.scrollIntoView({ block: "center" });
        return;
    }
    const nodeRect = el.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const target = Math.max(
        0,
        scroller.scrollTop +
            (nodeRect.top - scRect.top) -
            (topOffset != null
                ? topOffset
                : scroller.clientHeight / 2 - nodeRect.height / 2)
    );
    scrollTopTo(scroller, target, smooth);
}

/** Révélation de la bulle (parité mobile) : ouverte sur un nœud du bas de
 * l'écran, elle déborde sous le bord — on défile juste assez pour la voir
 * entière, jamais plus. Sa hauteur dépend de son contenu : à appeler APRÈS le
 * rendu. Le clamp au fond du conteneur évite de viser dans le vide — la bulle
 * est en superposition, elle n'allonge pas le contenu du chemin. */
function revealCallout(el) {
    const scroller = scrollParent(el);
    if (!scroller) {
        el.scrollIntoView({ block: "nearest" });
        return;
    }
    const rect = el.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const hidden = rect.bottom + CALLOUT_CLEARANCE - scRect.bottom;
    if (hidden <= 0) return;
    const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scrollTopTo(scroller, Math.min(maxTop, scroller.scrollTop + hidden), true);
}

export default function RepsPathClient({ track, isAdmin = false }) {
    const t = useTranslations();
    // Slugs terminés = source de vérité serveur (aucun stockage local).
    const completed = new Set(track.progress?.completedLessonSlugs || []);
    const [showWelcome, setShowWelcome] = useState(false);
    // Callout ouvert (code canonique de la leçon) — un seul à la fois.
    const [openCode, setOpenCode] = useState(null);
    // Rafale (jeu de paires chronométré), lancée depuis son NŒUD du chemin :
    // { unitKey, slug } du nœud joué. Chaque partie terminée est déclarée au
    // serveur — rejeu inclus : elle compte comme l'activité du jour (série
    // créditée au plus une fois par jour, journal plafonné côté back).
    const [madness, setMadness] = useState(null);
    // Saut d'unité (abonnés) : test de l'unité COURANTE lancé depuis sa
    // bannière — { unitKey, unitTitle }. Avantage payant : le non-abonné voit
    // la porte verrouillée et part sur /pricing (le verrou réel est serveur).
    const [skipTest, setSkipTest] = useState(null);
    const { data: session } = useSession();
    const router = useRouter();
    const madnessDoneRef = useRef(false);
    async function completeMadnessNode() {
        if (!madness || madnessDoneRef.current) return;
        madnessDoneRef.current = true;
        try {
            await fetch("/api/reps/complete", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    slug: madness.slug,
                    // Identité du nœud : le slug ne dit qu'une position.
                    contentKey: madness.contentKey || null,
                }),
            });
        } catch (e) {
            /* le jeu reste jouable, la complétion retentera à la prochaine partie */
            madnessDoneRef.current = false;
        }
    }
    // Défis légendaires réussis : marqueur LOCAL au navigateur (parité
    // mobile — décoration, pas de vérité serveur). Lu après l'hydratation
    // mais AVANT le premier paint (useLayoutEffect) : le nœud or ne doit pas
    // flasher orange à l'arrivée. useEffect côté serveur (SSR sans layout).
    const useClientLayoutEffect =
        typeof window === "undefined" ? useEffect : useLayoutEffect;
    const [legendary, setLegendary] = useState({});
    useClientLayoutEffect(() => {
        setLegendary(legendaryDoneMap());
    }, []);

    // Arrivée depuis un paiement Believemy Essential : on remercie, puis on nettoie
    // l'URL pour ne pas réafficher la modale au rechargement.
    useEffect(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            const type = p.get("subscription_type");
            if (
                p.get("subscription_started") === "1" &&
                (type === "essential" || type === "essential-annually")
            ) {
                setShowWelcome(true);
                window.history.replaceState(null, "", window.location.pathname);
            }
        } catch (e) {
            // ignore
        }
    }, []);

    // La progression serveur est exprimée en CODES canoniques de leçon
    // (identiques dans toutes les langues) ; lesson.slug est le slug localisé
    // réservé aux URLs. Repli sur slug pour les vieux payloads.
    const keyOf = (l) => l.code || l.slug;

    // Déblocage séquentiel (parité mobile) : la chaîne traverse les unités ;
    // une seule leçon "current" (la première déverrouillée non faite).
    let prevDone = true; // la toute première leçon est déverrouillée
    let currentFound = false;
    // Sens du premier virage de l'unité : la première part vers la droite,
    // les suivantes repartent du côté opposé au dernier virage de la
    // précédente (le chemin est une seule ligne, pas un damier par unité).
    let unitSign = 1;
    const units = track.units.map((unit, ui) => {
        // Le boss = le DERNIER nœud de type "lesson" de l'unité. Un nœud rappel
        // ou rafale placé plus loin ne lui vole pas le trophée (sinon le boss
        // hérite d'une icône de leçon et le nœud d'après porte la coupe).
        let lastLessonIdx = -1;
        unit.lessons.forEach((l, i) => {
            if ((l.kind || "lesson") === "lesson") lastLessonIdx = i;
        });
        // Forme du chemin de l'unité : autant de virages ENTIERS que sa
        // longueur le permet, premier et dernier nœud au centre.
        const offsets = unitOffsets(unit.lessons.length, unitSign);
        const slots = unitDecorationSlots(unit.lessons.length, unitSign);
        unitSign = nextUnitSign(unit.lessons.length, unitSign);
        const nodes = unit.lessons.map((lesson, li) => {
            const done = completed.has(keyOf(lesson));
            // Admin : jamais verrouillé. Une leçon non faite reste jouable
            // ("unlocked"), tout en conservant la vraie "current" et les "done"
            // pour garder une vision fidèle de la progression réelle.
            let status = isAdmin ? "unlocked" : "locked";
            if (done) {
                status = "done";
            } else if (prevDone && !currentFound) {
                status = "current";
                currentFound = true;
            }
            prevDone = done;
            return {
                lesson,
                kind: lesson.kind || "lesson",
                status,
                isBoss: li === lastLessonIdx,
                offset: offsets[li],
            };
        });
        return {
            key: unit.key,
            number: unitNumber(unit.key, ui),
            title: unit.title,
            // Jusqu'à deux décors : un par virage, dans l'ordre du chemin.
            decorations: decorationsForUnit(unit, slots),
            done: nodes.filter((n) => n.status === "done").length,
            total: nodes.length,
            nodes,
        };
    });

    const hasCurrent = units.some((u) =>
        u.nodes.some((n) => n.status === "current")
    );
    const allDone =
        units.length > 0 &&
        units.every((u) => u.done === u.total && u.total > 0);

    // --- Centrage automatique sur la leçon courante (parité Duolingo) :
    // positionnement instantané à l'arrivée sur le parcours, rejoué quand on
    // change de parcours (le composant reste monté, seule la prop change).
    const currentNodeRef = useRef(null);
    const trackSlug = track.slug || track.code || "";
    useEffect(() => {
        if (!currentNodeRef.current) return undefined;
        centerInScroller(currentNodeRef.current, false);
        // Seconde passe différée : en navigation client, le router Next
        // repositionne le segment APRÈS nos effets — on re-centre par-dessus
        // (sans effet quand la position est déjà bonne).
        const id = window.setTimeout(() => {
            if (currentNodeRef.current) {
                centerInScroller(currentNodeRef.current, false);
            }
        }, 150);
        return () => window.clearTimeout(id);
    }, [trackSlug]);

    // --- Bouton de recentrage : visible quand le nœud courant est hors écran,
    // flèche selon la direction (parité mobile : écoute du scroll, throttlée
    // par requestAnimationFrame).
    const [fab, setFab] = useState(null);
    useEffect(() => {
        const el = currentNodeRef.current;
        if (!el) {
            setFab(null);
            return undefined;
        }
        const scroller = scrollParent(el);
        if (!scroller) return undefined;
        let raf = 0;
        const update = () => {
            raf = 0;
            const node = currentNodeRef.current;
            if (!node) {
                setFab(null);
                return;
            }
            const nodeRect = node.getBoundingClientRect();
            const scRect = scroller.getBoundingClientRect();
            // Le header du site (sticky, 65px) recouvre le haut du scroller :
            // un nœud caché dessous compte comme hors écran.
            const next =
                nodeRect.bottom < scRect.top + STICKY_TOP
                    ? "up"
                    : nodeRect.top > scRect.bottom
                      ? "down"
                      : null;
            setFab((prev) => (prev === next ? prev : next));
        };
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };
        update();
        scroller.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            scroller.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [trackSlug]);

    function recenter() {
        if (currentNodeRef.current) {
            centerInScroller(currentNodeRef.current, true, RECENTER_OFFSET);
        }
    }

    // --- Ouverture d'une bulle : si elle dépasse sous le bord de l'écran, on
    // défile juste assez pour la voir entière (parité mobile). La mesure se
    // fait après le rendu — un seul callout ouvert, donc une seule ref.
    const calloutRef = useRef(null);
    useEffect(() => {
        if (openCode && calloutRef.current) {
            revealCallout(calloutRef.current);
        }
    }, [openCode]);

    // --- Le défilement referme la bulle (parité mobile : onScrollBeginDrag).
    // Sans ça, la bulle et son nœud restent posés par-dessus la bannière
    // d'unité collée en haut, et le chemin défile derrière eux.
    // On écoute les GESTES de l'utilisateur (molette, doigt, touches de
    // défilement) et jamais l'événement "scroll" : les défilements PROGRAMMÉS —
    // révélation de la bulle qu'on vient d'ouvrir, recentrage — la refermeraient
    // aussitôt.
    const rootRef = useRef(null);
    useEffect(() => {
        if (!openCode || !rootRef.current) return undefined;
        const close = () => setOpenCode(null);
        const onKeyDown = (e) => {
            if (SCROLL_KEYS.has(e.key)) close();
        };
        const target = scrollParent(rootRef.current) || window;
        target.addEventListener("wheel", close, { passive: true });
        target.addEventListener("touchmove", close, { passive: true });
        window.addEventListener("keydown", onKeyDown);
        return () => {
            target.removeEventListener("wheel", close);
            target.removeEventListener("touchmove", close);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [openCode]);

    // Changement de parcours (le composant reste monté, seule la prop change) :
    // on referme le callout et on cache le bouton de recentrage (ajustement
    // d'état au rendu, comme sur mobile — pas d'effet).
    const [prevTrackSlug, setPrevTrackSlug] = useState(trackSlug);
    if (prevTrackSlug !== trackSlug) {
        setPrevTrackSlug(trackSlug);
        setOpenCode(null);
        setFab(null);
    }

    return (
        /* Colonne du path façon Duolingo : centrée, largeur contenue. Tout tap
           hors d'un nœud (bannières comprises) referme le callout ouvert. */
        <div
            ref={rootRef}
            className="relative mx-auto w-full max-w-[560px]"
            onClick={() => setOpenCode(null)}
            role="presentation"
        >
            {units.map((unit) => (
                <section key={unit.key}>
                    {/* Bannière d'unité STICKY (agenda / Duolingo) : collée sous
                        le header du site jusqu'à ce que la suivante la pousse.
                        Le padding est porté par le wrapper (transparent ET
                        pointer-events-none : il ne doit jamais avaler les clics
                        des nœuds qui passent dessous), la carte porte l'ombre
                        pour se détacher des nœuds. */}
                    <div
                        className="pointer-events-none sticky z-30 pb-7 pt-2"
                        style={{ top: STICKY_TOP }}
                    >
                        <div
                            className="pointer-events-auto flex items-center gap-3 rounded-2xl p-4 shadow-[0_4px_10px_rgba(0,0,0,0.18)]"
                            style={{ backgroundColor: track.color || "#F97316" }}
                        >
                            <div className="min-w-0 flex-1">
                                <p className="text-[11px] font-bold uppercase tracking-[2px] text-white/80">
                                    {t("reps_section_unit", {
                                        number: unit.number,
                                    })}
                                </p>
                                <p className="mt-0.5 line-clamp-2 text-lg font-extrabold leading-snug text-white">
                                    {unit.title}
                                </p>
                            </div>
                            {/* Porte de saut : UNIQUEMENT l'unité courante,
                                visible pour tout connecté mais verrouillée
                                sans abonnement (avantage payant — le verrou
                                réel est côté serveur). */}
                            {session?.user &&
                                unit.nodes.some(
                                    (n) => n.status === "current"
                                ) &&
                                (hasUnitSkip(session.user) ? (
                                    <button
                                        type="button"
                                        title={t("reps_unit_skip_badge")}
                                        aria-label={t("reps_unit_skip_badge")}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenCode(null);
                                            setSkipTest({
                                                unitKey: unit.key,
                                                unitTitle: unit.title,
                                            });
                                        }}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
                                    >
                                        <SolarIcon
                                            name="double-alt-arrow-down-bold"
                                            className="text-xl"
                                        />
                                    </button>
                                ) : (
                                    <Link
                                        href={{
                                            pathname: "/pricing",
                                            query: { checkout: "essential" },
                                        }}
                                        title={t("reps_unit_skip_badge")}
                                        aria-label={t("reps_unit_skip_badge")}
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
                                    >
                                        <SolarIcon
                                            name="lock-keyhole-bold"
                                            className="text-lg"
                                        />
                                    </Link>
                                ))}
                            <span className="shrink-0 rounded-full bg-white/15 px-2.5 py-1 text-sm font-bold text-white/90">
                                {unit.done}/{unit.total}
                            </span>
                        </div>
                    </div>

                    <div>
                        {unit.nodes.map((node, ni) => (
                            <Fragment key={keyOf(node.lesson)}>
                                {unit.decorations.map((decoration, di) =>
                                    decoration.afterNode === ni ? (
                                        <RepsPathDecoration
                                            key={di}
                                            decoration={decoration}
                                            locked={unit.nodes.every(
                                                (n) => n.status === "locked"
                                            )}
                                        />
                                    ) : null
                                )}
                                {/* Le callout est en SUPERPOSITION (absolu) : il
                                    ne pousse jamais le layout. Ce conteneur
                                    n'est PAS surélevé (pas de zIndex, donc pas
                                    de contexte d'empilement) : le nœud, lui,
                                    doit passer SOUS la bannière collée en haut
                                    comme n'importe quel autre. */}
                                <div
                                    ref={
                                        node.status === "current"
                                            ? currentNodeRef
                                            : undefined
                                    }
                                    className="relative"
                                >
                                    <PathNode
                                        status={node.status}
                                        kind={node.kind}
                                        isBoss={node.isBoss}
                                        offset={node.offset}
                                        gold={
                                            node.status === "done" &&
                                            legendary[keyOf(node.lesson)] ===
                                                true
                                        }
                                        title={node.lesson.title}
                                        startLabel={t("reps_path_start")}
                                        onClick={() =>
                                            setOpenCode((prev) =>
                                                prev === keyOf(node.lesson)
                                                    ? null
                                                    : keyOf(node.lesson)
                                            )
                                        }
                                    />
                                    {openCode === keyOf(node.lesson) ? (
                                        /* Seule la bulle est surélevée, au-dessus
                                           des bannières sticky (z-30) : celle du
                                           dernier nœud d'une unité passerait
                                           sinon SOUS la bannière de l'unité
                                           suivante. 35 : sous le FAB (40) et le
                                           dropdown du sélecteur (45). */
                                        <div
                                            ref={calloutRef}
                                            className="absolute left-0 right-0 top-full z-[35] mt-3.5"
                                        >
                                            <LessonCallout
                                                lesson={node.lesson}
                                                kind={node.kind}
                                                status={node.status}
                                                offset={node.offset}
                                                legendaryDone={
                                                    legendary[
                                                        keyOf(node.lesson)
                                                    ] === true
                                                }
                                                onRush={() =>
                                                    setMadness({
                                                        unitKey: unit.key,
                                                        slug: node.lesson.slug,
                                                        contentKey:
                                                            node.lesson
                                                                .contentKey ||
                                                            null,
                                                        alreadyDone:
                                                            node.status ===
                                                            "done",
                                                    })
                                                }
                                                t={t}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </Fragment>
                        ))}
                    </div>
                </section>
            ))}

            {/* Fin de parcours. allDone : carte de célébration. Sinon : pied de
                chemin sobre qui rassure (« d'autres leçons arrivent bientôt »)
                pour que le bas du chemin ne donne jamais l'impression d'un
                cul-de-sac — parité avec l'app mobile. */}
            {allDone ? (
                <div className="mb-4 flex flex-col items-center rounded-2xl border-2 border-orange-200 bg-orange-50 p-6 text-center dark:border-orange-500/30 dark:bg-orange-500/10">
                    <Image
                        src="/images/bebot/bebot-reps.png"
                        alt="BeBot"
                        width={109}
                        height={106}
                        className="h-[106px] w-auto"
                    />
                    <p className="mt-3 text-xl font-extrabold text-gray-900 dark:text-white">
                        {t("reps_path_all_done")}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                        {t("reps_path_all_done_sub")}
                    </p>
                </div>
            ) : (
                <div className="mb-4 flex flex-col items-center gap-2.5 pb-2 pt-4 text-center">
                    <div className="grid h-11 w-11 place-items-center rounded-full bg-orange-50 dark:bg-orange-500/10">
                        <SolarIcon
                            name="hourglass-line-linear"
                            className="text-xl text-orange-400"
                        />
                    </div>
                    <p className="mb-0 text-sm font-semibold text-gray-400 dark:text-grayDark-500">
                        {t("reps_path_more_soon")}
                    </p>
                </div>
            )}

            {/* FAB de recentrage : collé en bas de l'écran tant que le path
                défile (hauteur nulle dans le flux — aucun décalage). */}
            <div className="pointer-events-none sticky bottom-6 z-40 -mt-12 flex h-12 justify-end">
                {fab != null && hasCurrent ? (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            recenter();
                        }}
                        aria-label={t("reps_path_recenter")}
                        className="pointer-events-auto grid h-12 w-12 place-items-center rounded-full border border-gray-200 bg-white text-orange-500 shadow-[0_4px_10px_rgba(0,0,0,0.12)] transition-transform hover:scale-105 dark:border-white/10 dark:bg-bgDark-840"
                    >
                        <SolarIcon
                            name={
                                fab === "up"
                                    ? "alt-arrow-up-linear"
                                    : "alt-arrow-down-linear"
                            }
                            className="text-2xl"
                        />
                    </button>
                ) : null}
            </div>

            {/* Bienvenue après un paiement Believemy Essential */}
            <AnimatePresence>
                {showWelcome && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setShowWelcome(false)}
                        className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 backdrop-blur-[6px] sm:items-center sm:p-6"
                    >
                        <motion.div
                            initial={{ y: "100%", opacity: 0.6 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: "100%", opacity: 0 }}
                            transition={{
                                type: "spring",
                                damping: 32,
                                stiffness: 380,
                                mass: 0.75,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl dark:bg-bgDark-840 sm:mx-4 sm:max-w-md sm:rounded-3xl"
                        >
                            <div className="flex items-end justify-center bg-gradient-to-b from-orange-50 to-white pt-8 dark:from-orange-500/10 dark:to-transparent">
                                <Image
                                    src="/images/bebot/bebot-happyx2k.png"
                                    alt="BeBot"
                                    width={122}
                                    height={144}
                                    className="h-36 w-auto"
                                />
                            </div>
                            <div className="px-7 pb-8 pt-5 text-center">
                                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.3em] text-orange-500">
                                    {t("reps_welcome_essential_badge")}
                                </p>
                                <h2 className="mb-3 text-[22px] font-bold leading-tight tracking-tight text-gray-900 dark:text-white">
                                    {t("reps_welcome_essential_title")}
                                </h2>
                                <p className="mb-7 text-[14px] leading-relaxed text-gray-500 dark:text-grayDark-400">
                                    {t("reps_welcome_essential_message")}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowWelcome(false)}
                                    className="w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition-colors hover:bg-primary/90"
                                >
                                    {t("reps_welcome_essential_cta")}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Rafale : le jeu de paires du nœud ⚡ du chemin. Chaque partie
                terminée est déclarée (rejeu inclus : activité du jour) ; le
                refresh au retour met la progression du chemin à jour. */}
            {madness && (
                <RepsMatchMadness
                    trackSlug={trackSlug}
                    unitKey={madness.unitKey}
                    onComplete={completeMadnessNode}
                    onClose={() => {
                        setMadness(null);
                        madnessDoneRef.current = false;
                        router.refresh();
                    }}
                />
            )}

            {/* Test de saut d'unité : le refresh au retour recharge la
                progression serveur — l'unité passée est done, la porte
                suivante est immédiatement disponible. */}
            {skipTest && (
                <RepsUnitSkip
                    trackSlug={trackSlug}
                    unitKey={skipTest.unitKey}
                    unitTitle={skipTest.unitTitle}
                    onPassed={() => {}}
                    onClose={() => {
                        setSkipTest(null);
                        router.refresh();
                    }}
                />
            )}
        </div>
    );
}

/** Pill flottante "COMMENCER" au-dessus du nœud courant (flottement doux). */
function StartPill({ label }) {
    return (
        <motion.div
            animate={{ y: [-3, 3] }}
            transition={{
                duration: 1.2,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
            }}
            className="mb-1.5 flex flex-col items-center"
        >
            <span className="rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[2px] text-orange-500 shadow-[0_3px_8px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-bgDark-840">
                {label}
            </span>
            {/* Petite flèche sous la pill, pointée vers le nœud. */}
            <span className="-mt-px h-0 w-0 border-x-[6px] border-t-[6px] border-x-transparent border-t-white dark:border-t-bgDark-840" />
        </motion.div>
    );
}

/**
 * Nœud 3D du path (même mécanique que le mobile) : face ELLIPTIQUE écrasée
 * (perspective Duolingo, léger surplomb), lèvre foncée en dessous, face qui
 * s'enfonce au press. Le nœud courant est plus grand et porte la pill
 * flottante "COMMENCER" au-dessus.
 */
function PathNode({
    status,
    isBoss,
    offset,
    gold,
    title,
    startLabel,
    onClick,
    kind = "lesson",
}) {
    const locked = status === "locked";
    const size = status === "current" ? 76 : 64;
    const faceH = Math.round(size * NODE_SQUASH);
    // Trophée permanent : le nœud passé en légendaire porte l'étoile sur or.
    // Nœuds typés (grammaire Duolingo) : le rappel porte l'haltère, la rafale
    // l'éclair — l'icône dit la NATURE du nœud, le statut dit son état.
    const icon = locked
        ? { name: "lock-keyhole-minimalistic-bold", px: 26 }
        : kind === "recall"
          ? { name: "dumbbell-large-minimalistic-bold", px: 30 }
          : kind === "rush"
            ? { name: "bolt-bold", px: 30 }
            : gold
              ? { name: "star-bold", px: 32 }
              : isBoss
                ? { name: "cup-star-bold", px: status === "current" ? 32 : 30 }
                : status === "current"
                  ? { name: "star-bold", px: 32 }
                  : status === "unlocked"
                    ? { name: "play-circle-bold", px: 28 }
                    : { name: "check-circle-bold", px: 30 };
    const face = gold ? LEGENDARY_COLORS.base : "#F97316";
    const lip = gold ? LEGENDARY_COLORS.lip : "#C2410C";
    // Stries de complétion (parité Duolingo) : bandes diagonales claires en
    // travers de la face d'un nœud TERMINÉ — orange strié = fait, or strié =
    // fait en légendaire. La face non terminée reste unie.
    const stripes =
        !locked && status === "done"
            ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.16) 0px, rgba(255,255,255,0.16) 11px, transparent 11px, transparent 24px)"
            : undefined;

    return (
        <div className="mb-7 flex justify-center">
            <div
                className="flex flex-col items-center"
                style={{ transform: `translateX(${offset}px)` }}
            >
                {status === "current" ? <StartPill label={startLabel} /> : null}
                <button
                    type="button"
                    aria-label={title}
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                    }}
                    className="group relative block"
                    style={{ width: size, height: faceH + NODE_LIP }}
                >
                    {/* Lèvre : la MÊME ellipse que la face, décalée vers le
                        bas (jamais une capsule plus haute — ses coins
                        dépasseraient au-dessus du nœud). borderRadius 50% en
                        style : rounded-full (9999px) ferait un stade, pas une
                        ellipse. */}
                    <span
                        className={`absolute left-0 block ${
                            locked ? "bg-gray-300 dark:bg-white/5" : ""
                        }`}
                        style={{
                            top: NODE_LIP,
                            width: size,
                            height: faceH,
                            borderRadius: "50%",
                            backgroundColor: locked ? undefined : lip,
                        }}
                    />
                    <span
                        className={`absolute left-0 top-0 grid place-items-center transition-transform duration-100 ${
                            locked
                                ? "bg-gray-200 dark:bg-white/10"
                                : "group-active:translate-y-1"
                        }`}
                        style={{
                            width: size,
                            height: faceH,
                            borderRadius: "50%",
                            backgroundColor: locked ? undefined : face,
                            backgroundImage: stripes,
                        }}
                    >
                        <SolarIcon
                            name={icon.name}
                            className={
                                locked ? "text-gray-400" : "text-white"
                            }
                            style={{
                                fontSize: icon.px,
                                transform: `scaleY(${NODE_SQUASH})`,
                            }}
                        />
                    </span>
                </button>
            </div>
        </div>
    );
}

/**
 * Callout ancré sous un nœud (parité mobile) : bulle colorée avec flèche vers
 * le haut alignée sur le nœud, titre de la leçon, et UN CTA pleine largeur
 * qui annonce la récompense. Version grise sans bouton pour les leçons
 * verrouillées. Leçon terminée non légendaire : double CTA façon Duolingo
 * (REVOIR blanc au-dessus, LÉGENDAIRE doré en dessous) ; une fois le défi
 * réussi, le nœud or suffit — on garde le seul REVOIR.
 */
function LessonCallout({
    lesson,
    kind = "lesson",
    status,
    offset,
    legendaryDone,
    onRush,
    t,
}) {
    const locked = status === "locked";
    const exCount =
        lesson.practiceCount ??
        (lesson.steps || lesson.exercises || []).filter(
            (s) => s.type !== "concept"
        ).length;
    const startXp = lesson.rewardXp ?? REWARD_XP;

    // Le CTA cliqué, le temps que la leçon se charge : c'est une vraie page qui
    // arrive, pas un panneau local. Sans ce repère, le callout restait figé et
    // on recliquait.
    const [pending, setPending] = useState(null);
    const reduce = useReducedMotion();
    const localizedRouter = useLocalizedRouter();
    // Un seul départ : une fois un CTA cliqué, les deux liens deviennent
    // inertes — on ne change pas de leçon en vol.
    const leavingRef = useRef(false);
    const fillTimerRef = useRef(null);
    const pushTimerRef = useRef(null);
    // Fermer le callout (ou cliquer un autre nœud) pendant le différé annule
    // le départ : pas de navigation fantôme depuis un composant démonté.
    useEffect(
        () => () => {
            clearTimeout(fillTimerRef.current);
            clearTimeout(pushTimerRef.current);
        },
        []
    );

    // Départ différé (voir NAV_FILL_MS/NAV_PUSH_MS) : on laisse la barre se
    // jouer en entier, PUIS on navigue. Le Link reste un vrai lien : les
    // clics modifiés (nouvel onglet…) gardent le comportement natif.
    const goWithFill = (e, key, href) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        if (leavingRef.current) {
            e.preventDefault();
            return;
        }
        leavingRef.current = true;
        setPending(key);
        // Mouvement réduit : remplissage statique, départ immédiat (différer
        // sans animation ne serait que de la latence).
        if (reduce) return;
        e.preventDefault();
        fillTimerRef.current = setTimeout(() => setPending(null), NAV_FILL_MS);
        pushTimerRef.current = setTimeout(
            () => localizedRouter.push(href),
            NAV_PUSH_MS
        );
    };

    const mainHref = {
        pathname: "/reps/[lessonSlug]",
        params: { lessonSlug: lesson.slug },
        // Révision : l'écran de fin promet le tarif révision (5), aligné
        // avec ce CTA.
        query: status === "done" ? { review: "1" } : undefined,
    };
    const legendaryHref = {
        pathname: "/reps/[lessonSlug]",
        params: { lessonSlug: lesson.slug },
        query: { legendary: "1" },
    };
    // Libellés sortis de la ligne : la barre les rejoue à l'identique par
    // dessus, et deux textes qui divergent, c'est un décalage à l'écran.
    const mainLabel =
        status === "done"
            ? t("reps_path_cta_review", { xp: REVIEW_XP })
            : t("reps_path_cta_start", { xp: startXp });
    const legendaryLabel = t("reps_path_cta_legendary", { xp: LEGENDARY_XP });
    // Recopié sur les deux couches du CTA : même boîte, même police.
    const ctaOverlay =
        "flex items-center justify-center py-3.5 text-base font-extrabold uppercase";

    return (
        <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.8 }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Flèche triangle vers le haut, alignée sur le nœud cliqué. */}
            <div
                className={`mx-auto h-0 w-0 border-x-[9px] border-b-[9px] border-x-transparent ${
                    locked
                        ? "border-b-gray-100 dark:border-b-white/10"
                        : "border-b-orange-500"
                }`}
                style={{ transform: `translateX(${offset}px)` }}
            />
            <div
                className={`rounded-2xl p-4 shadow-[0_8px_14px_rgba(0,0,0,0.18)] ${
                    locked
                        ? "bg-gray-100 dark:bg-white/10"
                        : "bg-orange-500"
                }`}
            >
                <p
                    className={`text-base font-extrabold ${
                        locked
                            ? "text-gray-500 dark:text-grayDark-400"
                            : "text-white"
                    }`}
                >
                    {lesson.title}
                </p>
                {locked ? (
                    <p className="mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                        {t("reps_path_locked_hint")}
                    </p>
                ) : kind === "rush" ? (
                    <>
                        {/* Nœud Rafale : le jeu de paires s'ouvre ICI, jamais le player. */}
                        <p className="mt-0.5 text-sm text-white/80">
                            {t("reps_madness_subtitle", { seconds: 45 })}
                        </p>
                        <button
                            type="button"
                            onClick={onRush}
                            className="mt-3.5 block w-full rounded-2xl border-b-4 border-gray-300 bg-white py-3.5 text-center text-base font-extrabold uppercase text-orange-500 active:translate-y-1 active:border-b-transparent"
                        >
                            {t("reps_path_cta_rush")}
                        </button>
                    </>
                ) : (
                    <>
                        {kind === "recall" ? (
                            <p className="mt-0.5 text-sm text-white/80">
                                {t("reps_path_recall_hint")}
                            </p>
                        ) : exCount > 0 ? (
                            <p className="mt-0.5 text-sm text-white/80">
                                {t("reps_lesson_exercises_count", {
                                    count: exCount,
                                })}
                            </p>
                        ) : null}
                        <Link
                            href={mainHref}
                            onClick={(e) => goWithFill(e, "main", mainHref)}
                            className="relative mt-3.5 block overflow-hidden rounded-2xl border-b-4 border-gray-300 bg-white py-3.5 text-center text-base font-extrabold uppercase text-orange-500 hover:text-orange-500 active:translate-y-1 active:border-b-transparent"
                        >
                            {mainLabel}
                            {/* Bouton blanc : la barre est sa LÈVRE grise qui
                                monte, le texte reste orange. Surtout pas
                                l'orange de la bulle : rempli, le bouton
                                fusionnait avec le callout. */}
                            <ProgressFill
                                loading={pending === "main"}
                                fillBg="#d1d5db"
                                fillText="#f97316"
                                className={ctaOverlay}
                            >
                                {mainLabel}
                            </ProgressFill>
                        </Link>
                        {kind === "lesson" && status === "done" && !legendaryDone ? (
                            <Link
                                href={legendaryHref}
                                onClick={(e) =>
                                    goWithFill(e, "legendary", legendaryHref)
                                }
                                className="relative mt-3 block overflow-hidden rounded-2xl border-b-4 border-b-[#DFAE35] py-3.5 text-center text-base font-extrabold uppercase text-[#6A3D00] hover:text-[#6A3D00] active:translate-y-1 active:border-b-transparent"
                                style={{ background: LEGENDARY_FACETS }}
                            >
                                {legendaryLabel}
                                {/* Le bouton s'INVERSE au passage de la barre :
                                    fond brun du texte, libellé qui bascule en
                                    or brillant. Les facettes restent visibles
                                    sur la portion à venir, et la lèvre dorée
                                    reste dessous : c'est toujours l'or
                                    légendaire, jamais un jaune terni. */}
                                <ProgressFill
                                    loading={pending === "legendary"}
                                    fillBg={LEGENDARY_COLORS.text}
                                    fillText={LEGENDARY_COLORS.shine}
                                    className={ctaOverlay}
                                >
                                    {legendaryLabel}
                                </ProgressFill>
                            </Link>
                        ) : null}
                    </>
                )}
            </div>
        </motion.div>
    );
}
